import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { TimePicker } from '@/components/ui/time-picker';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Loader2, Plus, Trash2, MapPin, Truck, Route, Users, Camera, 
  FileCheck, Upload, Navigation, Clock, CheckCircle, X, Image
} from 'lucide-react';
import { DatePickerField } from '@/components/shipment';

const LOAD_TYPES = [
  { value: 'FTL', label: 'FTL (Full Truckload)' },
  { value: 'LTL', label: 'LTL (Less Than Truckload)' },
  { value: 'Partial', label: 'Partial Load' },
  { value: 'Dedicated', label: 'Dedicated' }
];

const LOCATION_TYPES = [
  { value: 'Customer', label: 'Customer' },
  { value: 'Warehouse', label: 'Warehouse' },
  { value: 'Distribution Center', label: 'Distribution Center' },
  { value: 'Yard', label: 'Yard' },
  { value: 'Port', label: 'Port' },
  { value: 'Terminal', label: 'Terminal' },
  { value: 'Cross Dock', label: 'Cross Dock' }
];

const STATUS_OPTIONS = [
  'New Load', 'Dispatched', 'In Transit', 'At Pickup', 'At Delivery', 
  'Delivered', 'Completed', 'Cancelled', 'On Hold'
];

const STATUS_COLORS = {
  'New Load': '#E6E6FA',
  'Dispatched': '#90EE90',
  'In Transit': '#FFB6C1',
  'At Pickup': '#FFDAB9',
  'At Delivery': '#87CEEB',
  'Delivered': '#98FB98',
  'Completed': '#D3D3D3',
  'Cancelled': '#FA8072',
  'On Hold': '#FFD700'
};

export const EditLocalLoad = ({ open, onOpenChange, load, masterData, customers = [], onSuccess }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [statusHistory, setStatusHistory] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [trailerTypes, setTrailerTypes] = useState([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Route Optimization
  const [optimizing, setOptimizing] = useState(false);
  const [routeStats, setRouteStats] = useState(null);
  
  // Nearby Drivers
  const [nearbyDrivers, setNearbyDrivers] = useState([]);
  const [findingDrivers, setFindingDrivers] = useState(false);
  
  // POD (Proof of Delivery)
  const [pods, setPods] = useState([]);
  const [loadingPods, setLoadingPods] = useState(false);
  const [podDialogOpen, setPodDialogOpen] = useState(false);
  const [uploadingPod, setUploadingPod] = useState(false);
  const [podForm, setPodForm] = useState({
    recipient_name: '',
    notes: '',
    signature_data: null,
    photos: []
  });
  const podFileInputRef = useRef(null);

  useEffect(() => {
    if (load) {
      setFormData({
        ...load,
        pickup_date: load.pickup_date ? new Date(load.pickup_date) : null,
        delivery_date: load.delivery_date ? new Date(load.delivery_date) : null
      });
      setRouteStats(load.route_stats || null);
      fetchStatusHistory();
      fetchEquipment();
      fetchPods();
    }
  }, [load]);

  const fetchStatusHistory = async () => {
    try {
      const response = await api.get(`/local-loads/${load.id}/status-history`);
      setStatusHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch status history');
    }
  };

  const fetchEquipment = async () => {
    try {
      const [trucksRes, trailersRes, typesRes] = await Promise.all([
        api.get('/local-loads/equipment/trucks'),
        api.get('/local-loads/equipment/trailers'),
        api.get('/local-loads/equipment/trailer-types')
      ]);
      setTrucks(trucksRes.data || []);
      setTrailers(trailersRes.data || []);
      setTrailerTypes(typesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch equipment');
    }
  };

  const fetchPods = async () => {
    if (!load?.id) return;
    setLoadingPods(true);
    try {
      const response = await api.get(`/local-loads/${load.id}/pod`);
      setPods(response.data?.pods || []);
    } catch (error) {
      console.error('Failed to fetch PODs');
    } finally {
      setLoadingPods(false);
    }
  };

  // Route Optimization
  const handleOptimizeRoute = async () => {
    if (!formData.stops || formData.stops.length < 2) {
      toast.error('Need at least 2 stops to optimize route');
      return;
    }
    
    setOptimizing(true);
    try {
      const response = await api.post(`/local-loads/${load.id}/optimize-stops`);
      const { optimized_stops, stats } = response.data;
      
      setFormData(prev => ({ ...prev, stops: optimized_stops }));
      setRouteStats(stats);
      toast.success(`Route optimized! Total: ${stats.total_distance} miles, ~${stats.total_time} mins`);
    } catch (error) {
      toast.error('Failed to optimize route');
    } finally {
      setOptimizing(false);
    }
  };

  // Find Nearby Drivers
  const handleFindNearbyDrivers = async () => {
    // Use pickup location coordinates if available
    const lat = formData.pickup_lat;
    const lng = formData.pickup_lng;
    
    if (!lat || !lng) {
      toast.error('Pickup location coordinates not available. Please set pickup address with coordinates.');
      return;
    }
    
    setFindingDrivers(true);
    try {
      const response = await api.post('/local-loads/find-nearest-drivers', {
        pickup_lat: lat,
        pickup_lng: lng,
        max_distance_miles: 100
      });
      setNearbyDrivers(response.data?.drivers || []);
      if (response.data?.drivers?.length === 0) {
        toast.info('No nearby drivers found within 100 miles');
      }
    } catch (error) {
      toast.error('Failed to find nearby drivers');
    } finally {
      setFindingDrivers(false);
    }
  };

  const handleAssignDriver = (driver) => {
    handleChange('driver_id', driver.id);
    handleChange('driver_name', driver.name);
    toast.success(`Assigned ${driver.name} (${driver.distance_miles || '?'} miles away)`);
  };

  // POD Management
  const handlePodPhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPodForm(prev => ({
          ...prev,
          photos: [...prev.photos, event.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleCreatePod = async () => {
    if (!podForm.recipient_name) {
      toast.error('Recipient name is required');
      return;
    }
    
    setUploadingPod(true);
    try {
      await api.post(`/local-loads/${load.id}/pod`, {
        recipient_name: podForm.recipient_name,
        notes: podForm.notes,
        signature_data: podForm.signature_data,
        photos: podForm.photos,
        location: formData.delivery_lat && formData.delivery_lng ? {
          lat: formData.delivery_lat,
          lng: formData.delivery_lng
        } : null
      });
      
      toast.success('Proof of Delivery created');
      setPodDialogOpen(false);
      setPodForm({ recipient_name: '', notes: '', signature_data: null, photos: [] });
      fetchPods();
    } catch (error) {
      toast.error('Failed to create POD');
    } finally {
      setUploadingPod(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = { ...formData };
      delete payload.id;
      delete payload.job_number;
      delete payload.created_at;
      delete payload.created_by;
      delete payload.company_id;
      
      if (payload.pickup_date instanceof Date) payload.pickup_date = payload.pickup_date.toISOString();
      if (payload.delivery_date instanceof Date) payload.delivery_date = payload.delivery_date.toISOString();

      await api.put(`/local-loads/${load.id}`, payload);
      toast.success('Load updated');
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to update load');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }
    try {
      await api.post(`/local-loads/${load.id}/status`, {
        status: newStatus,
        message: statusMessage || `Status changed to ${newStatus}`,
        driver_id: formData.driver_id,
        driver_name: formData.driver_name
      });
      setFormData(prev => ({ ...prev, status: newStatus }));
      setStatusDialogOpen(false);
      setNewStatus('');
      setStatusMessage('');
      fetchStatusHistory();
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/local-loads/${load.id}`);
      toast.success('Load deleted');
      setDeleteDialogOpen(false);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to delete load');
    } finally {
      setDeleting(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  if (!load) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-2xl bg-slate-900 border-slate-800 overflow-y-auto p-0" data-testid="edit-local-load-sheet">
          <SheetHeader className="p-6 border-b border-slate-800">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading text-white flex items-center gap-2">
                <MapPin className="h-5 w-5 text-green-400" />
                Edit Local Load - {load.job_number}
              </SheetTitle>
              <Badge 
                style={{ backgroundColor: STATUS_COLORS[formData.status], color: '#1a1a1a' }}
                className="font-medium"
              >
                {formData.status}
              </Badge>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSubmit} disabled={loading} size="sm" className="bg-green-600 hover:bg-green-500">
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save
              </Button>
              <Button onClick={() => { handleSubmit(); onOpenChange(false); }} disabled={loading} size="sm" className="bg-blue-600 hover:bg-blue-500">
                Save & Exit
              </Button>
              <Button onClick={() => setDeleteDialogOpen(true)} variant="outline" size="sm" className="border-red-600 text-red-400">
                <Trash2 className="h-4 w-4 mr-1" />Delete
              </Button>
              <Button onClick={() => onOpenChange(false)} variant="outline" size="sm" className="border-slate-700">
                Exit
              </Button>
            </div>
          </SheetHeader>

          <Tabs defaultValue="status" className="w-full">
            <TabsList className="w-full justify-start rounded-none border-b border-slate-800 bg-transparent h-auto p-0">
              <TabsTrigger value="status" className="rounded-none border-b-2 border-transparent data-[state=active]:border-green-500 px-4 py-3 text-slate-400 data-[state=active]:text-white">
                Status History
              </TabsTrigger>
              <TabsTrigger value="details" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 px-4 py-3 text-slate-400 data-[state=active]:text-white">
                Load Details
              </TabsTrigger>
              <TabsTrigger value="equipment" className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-500 px-4 py-3 text-slate-400 data-[state=active]:text-white">
                Equipment
              </TabsTrigger>
              <TabsTrigger value="route" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 px-4 py-3 text-slate-400 data-[state=active]:text-white">
                <Route className="h-4 w-4 mr-1" />Route & POD
              </TabsTrigger>
            </TabsList>

            <TabsContent value="status" className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Button size="sm" className="bg-orange-500 hover:bg-orange-400" onClick={() => { setNewStatus(formData.status); setStatusDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-1" />Change Status
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Timestamp</TableHead>
                    <TableHead className="text-slate-400">Effective Date</TableHead>
                    <TableHead className="text-slate-400">Driver</TableHead>
                    <TableHead className="text-slate-400">Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statusHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">No status history yet</TableCell>
                    </TableRow>
                  ) : (
                    statusHistory.map(entry => (
                      <TableRow key={entry.id} className="border-slate-800">
                        <TableCell>
                          <Badge style={{ backgroundColor: STATUS_COLORS[entry.status], color: '#1a1a1a' }}>
                            {entry.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-300 text-xs">{formatDateTime(entry.created_at)}</TableCell>
                        <TableCell className="text-slate-300">{entry.effective_date || '-'}</TableCell>
                        <TableCell className="text-slate-300">{entry.driver_name || '-'}</TableCell>
                        <TableCell className="text-slate-300">{entry.message || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="details" className="p-6 space-y-6">
              {/* Load Type & Customer */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Load Type</Label>
                  <Select value={formData.load_type} onValueChange={(v) => handleChange('load_type', v)}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {LOAD_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Customer</Label>
                  <Input value={formData.customer_name || ''} onChange={(e) => handleChange('customer_name', e.target.value)} className="bg-slate-950 border-slate-800 text-white" />
                </div>
              </div>

              {/* Pickup */}
              <div className="space-y-4 p-4 bg-green-900/20 rounded-lg border border-green-500/30">
                <h3 className="font-medium text-green-400">Pickup</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DatePickerField label="Pickup Date" value={formData.pickup_date} onChange={handleChange} field="pickup_date" />
                  <div className="space-y-2">
                    <Label className="text-slate-300">Location Type</Label>
                    <Select value={formData.origin_type || "none"} onValueChange={(v) => handleChange('origin_type', v === "none" ? "" : v)}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="none" className="text-slate-400">Select</SelectItem>
                        {LOCATION_TYPES.map(t => (<SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Input value={formData.pickup_address || ''} onChange={(e) => handleChange('pickup_address', e.target.value)} placeholder="Address" className="bg-slate-950 border-slate-800 text-white" />
                <div className="grid grid-cols-3 gap-2">
                  <Input value={formData.pickup_city || ''} onChange={(e) => handleChange('pickup_city', e.target.value)} placeholder="City" className="bg-slate-950 border-slate-800 text-white" />
                  <Input value={formData.pickup_state || ''} onChange={(e) => handleChange('pickup_state', e.target.value)} placeholder="State" className="bg-slate-950 border-slate-800 text-white" />
                  <Input value={formData.pickup_zip || ''} onChange={(e) => handleChange('pickup_zip', e.target.value)} placeholder="ZIP" className="bg-slate-950 border-slate-800 text-white" />
                </div>
              </div>

              {/* Delivery */}
              <div className="space-y-4 p-4 bg-blue-900/20 rounded-lg border border-blue-500/30">
                <h3 className="font-medium text-blue-400">Delivery</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DatePickerField label="Delivery Date" value={formData.delivery_date} onChange={handleChange} field="delivery_date" />
                  <div className="space-y-2">
                    <Label className="text-slate-300">Location Type</Label>
                    <Select value={formData.destination_type || "none"} onValueChange={(v) => handleChange('destination_type', v === "none" ? "" : v)}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="none" className="text-slate-400">Select</SelectItem>
                        {LOCATION_TYPES.map(t => (<SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Input value={formData.delivery_address || ''} onChange={(e) => handleChange('delivery_address', e.target.value)} placeholder="Address" className="bg-slate-950 border-slate-800 text-white" />
                <div className="grid grid-cols-3 gap-2">
                  <Input value={formData.delivery_city || ''} onChange={(e) => handleChange('delivery_city', e.target.value)} placeholder="City" className="bg-slate-950 border-slate-800 text-white" />
                  <Input value={formData.delivery_state || ''} onChange={(e) => handleChange('delivery_state', e.target.value)} placeholder="State" className="bg-slate-950 border-slate-800 text-white" />
                  <Input value={formData.delivery_zip || ''} onChange={(e) => handleChange('delivery_zip', e.target.value)} placeholder="ZIP" className="bg-slate-950 border-slate-800 text-white" />
                </div>
              </div>

              {/* Rate & Cargo */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Rate ($)</Label>
                  <Input type="number" value={formData.rate || 0} onChange={(e) => handleChange('rate', parseFloat(e.target.value) || 0)} className="bg-slate-950 border-slate-800 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Weight (lbs)</Label>
                  <Input type="number" value={formData.weight || 0} onChange={(e) => handleChange('weight', parseFloat(e.target.value) || 0)} className="bg-slate-950 border-slate-800 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Commodity</Label>
                  <Input value={formData.commodity || ''} onChange={(e) => handleChange('commodity', e.target.value)} className="bg-slate-950 border-slate-800 text-white" />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-slate-300">Special Instructions</Label>
                <Textarea value={formData.special_instructions || ''} onChange={(e) => handleChange('special_instructions', e.target.value)} className="bg-slate-950 border-slate-800 text-white min-h-[80px]" />
              </div>
            </TabsContent>

            <TabsContent value="equipment" className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Driver</Label>
                  <Select value={formData.driver_id || "none"} onValueChange={(v) => {
                    const driver = masterData?.drivers?.find(d => d.id === v);
                    handleChange('driver_id', v === "none" ? "" : v);
                    handleChange('driver_name', driver?.name || '');
                  }}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select driver" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="none" className="text-slate-400">No driver</SelectItem>
                      {masterData?.drivers?.map(d => (
                        <SelectItem key={d.id} value={d.id} className="text-white">{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Trailer Type</Label>
                  <Select value={formData.trailer_type || "none"} onValueChange={(v) => handleChange('trailer_type', v === "none" ? "" : v)}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="none" className="text-slate-400">Select type</SelectItem>
                      {trailerTypes.map(t => (
                        <SelectItem key={t.id} value={t.name} className="text-white">{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Truck</Label>
                  <Select value={formData.truck_id || "none"} onValueChange={(v) => handleChange('truck_id', v === "none" ? "" : v)}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select truck" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="none" className="text-slate-400">No truck</SelectItem>
                      {trucks.map(t => (
                        <SelectItem key={t.id} value={t.id} className="text-white">{t.number} - {t.make} {t.model}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Trailer</Label>
                  <Select value={formData.trailer_id || "none"} onValueChange={(v) => handleChange('trailer_id', v === "none" ? "" : v)}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select trailer" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="none" className="text-slate-400">No trailer</SelectItem>
                      {trailers.map(t => (
                        <SelectItem key={t.id} value={t.id} className="text-white">{t.number} - {t.type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Route & POD Tab */}
            <TabsContent value="route" className="p-6 space-y-6">
              {/* Route Optimization Section */}
              <div className="space-y-4 p-4 bg-orange-900/20 rounded-lg border border-orange-500/30">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-orange-400 flex items-center gap-2">
                    <Route className="h-5 w-5" />Route Optimization
                  </h3>
                  {formData.is_multi_stop && formData.stops?.length >= 2 && (
                    <Button 
                      size="sm" 
                      onClick={handleOptimizeRoute} 
                      disabled={optimizing}
                      className="bg-orange-600 hover:bg-orange-500"
                      data-testid="optimize-route-btn"
                    >
                      {optimizing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Navigation className="h-4 w-4 mr-1" />}
                      Optimize Route
                    </Button>
                  )}
                </div>
                
                {routeStats && (
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-slate-800 rounded p-3">
                      <div className="text-xl font-bold text-orange-400">{routeStats.total_distance} mi</div>
                      <div className="text-xs text-slate-400">Total Distance</div>
                    </div>
                    <div className="bg-slate-800 rounded p-3">
                      <div className="text-xl font-bold text-blue-400">{routeStats.total_time} min</div>
                      <div className="text-xs text-slate-400">Est. Drive Time</div>
                    </div>
                    <div className="bg-slate-800 rounded p-3">
                      <div className="text-xl font-bold text-green-400">{routeStats.stops_count}</div>
                      <div className="text-xs text-slate-400">Stops</div>
                    </div>
                  </div>
                )}
                
                {formData.is_multi_stop && formData.stops?.length > 0 ? (
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-sm">Stop Sequence</Label>
                    {formData.stops.map((stop, idx) => (
                      <div key={stop.id || idx} className="flex items-center gap-3 p-2 bg-slate-800 rounded text-sm">
                        <Badge variant="outline" className="text-orange-400">{stop.sequence || idx + 1}</Badge>
                        <div className="flex-1">
                          <span className="text-white">{stop.address || 'No address'}</span>
                          <span className="text-slate-400 ml-2">{stop.city}, {stop.state}</span>
                        </div>
                        {stop.distance_from_prev && (
                          <span className="text-xs text-slate-500">+{stop.distance_from_prev} mi</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Enable "Multi-Stop Load" and add stops to use route optimization.</p>
                )}
              </div>

              {/* Nearby Drivers Section */}
              <div className="space-y-4 p-4 bg-purple-900/20 rounded-lg border border-purple-500/30">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-purple-400 flex items-center gap-2">
                    <Users className="h-5 w-5" />Find Nearby Drivers
                  </h3>
                  <Button 
                    size="sm" 
                    onClick={handleFindNearbyDrivers} 
                    disabled={findingDrivers}
                    variant="outline"
                    className="border-purple-500 text-purple-400"
                    data-testid="find-drivers-btn"
                  >
                    {findingDrivers ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MapPin className="h-4 w-4 mr-1" />}
                    Find Drivers
                  </Button>
                </div>
                
                {nearbyDrivers.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {nearbyDrivers.map(driver => (
                      <div key={driver.id} className="flex items-center justify-between p-2 bg-slate-800 rounded text-sm">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${driver.status === 'available' ? 'bg-green-500' : 'bg-amber-500'}`} />
                          <span className="text-white">{driver.name}</span>
                          {driver.has_location && (
                            <span className="text-xs text-slate-400">
                              {driver.distance_miles} mi away • ~{driver.eta_minutes} min ETA
                            </span>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleAssignDriver(driver)}
                          className="text-purple-400 hover:text-purple-300"
                        >
                          Assign
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Click "Find Drivers" to locate available drivers near the pickup location.</p>
                )}
              </div>

              {/* Proof of Delivery Section */}
              <div className="space-y-4 p-4 bg-green-900/20 rounded-lg border border-green-500/30">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-green-400 flex items-center gap-2">
                    <FileCheck className="h-5 w-5" />Proof of Delivery
                  </h3>
                  <Button 
                    size="sm" 
                    onClick={() => setPodDialogOpen(true)}
                    className="bg-green-600 hover:bg-green-500"
                    data-testid="create-pod-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />Create POD
                  </Button>
                </div>
                
                {loadingPods ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
                  </div>
                ) : pods.length > 0 ? (
                  <div className="space-y-2">
                    {pods.map(pod => (
                      <div key={pod.id} className="p-3 bg-slate-800 rounded">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="text-white font-medium">{pod.recipient_name}</span>
                          </div>
                          <span className="text-xs text-slate-400">
                            {pod.captured_at ? new Date(pod.captured_at).toLocaleString() : 'N/A'}
                          </span>
                        </div>
                        {pod.notes && <p className="text-slate-400 text-sm mt-1">{pod.notes}</p>}
                        {pod.photos?.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {pod.photos.slice(0, 3).map((photo, idx) => (
                              <div key={idx} className="w-12 h-12 bg-slate-700 rounded overflow-hidden">
                                <img src={photo} alt={`POD ${idx + 1}`} className="w-full h-full object-cover" />
                              </div>
                            ))}
                            {pod.photos.length > 3 && (
                              <div className="w-12 h-12 bg-slate-700 rounded flex items-center justify-center text-xs text-slate-400">
                                +{pod.photos.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No proof of delivery records yet. Create one when delivery is complete.</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* POD Creation Dialog */}
      <Dialog open={podDialogOpen} onOpenChange={setPodDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-green-400" />
              Create Proof of Delivery
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Record delivery confirmation for {load?.job_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Recipient Name *</Label>
              <Input 
                value={podForm.recipient_name} 
                onChange={(e) => setPodForm(prev => ({ ...prev, recipient_name: e.target.value }))}
                placeholder="Person who received delivery"
                className="bg-slate-950 border-slate-800 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Delivery Notes</Label>
              <Textarea 
                value={podForm.notes} 
                onChange={(e) => setPodForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any notes about the delivery..."
                className="bg-slate-950 border-slate-800 text-white min-h-[60px]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Photos</Label>
              <input 
                type="file" 
                ref={podFileInputRef}
                onChange={handlePodPhotoSelect}
                accept="image/*"
                multiple
                className="hidden"
              />
              <div className="flex gap-2 flex-wrap">
                {podForm.photos.map((photo, idx) => (
                  <div key={idx} className="relative w-16 h-16">
                    <img src={photo} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover rounded" />
                    <button 
                      type="button"
                      onClick={() => setPodForm(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }))}
                      className="absolute -top-1 -right-1 bg-red-600 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => podFileInputRef.current?.click()}
                  className="w-16 h-16 border-dashed border-slate-600"
                >
                  <Camera className="h-5 w-5 text-slate-400" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPodDialogOpen(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePod} 
              disabled={uploadingPod}
              className="bg-green-600 hover:bg-green-500"
            >
              {uploadingPod && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create POD
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Change Status</DialogTitle>
            <DialogDescription className="text-slate-400">Update the load status</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s} value={s} className="text-white">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: STATUS_COLORS[s] }} />
                        {s}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Message (optional)</Label>
              <Textarea value={statusMessage} onChange={(e) => setStatusMessage(e.target.value)} placeholder="Add a note..." className="bg-slate-950 border-slate-800 text-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)} className="border-slate-700">Cancel</Button>
            <Button onClick={handleStatusChange} className="bg-blue-600 hover:bg-blue-500">Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete Load</DialogTitle>
            <DialogDescription className="text-slate-400">Are you sure you want to delete this load? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-3 bg-slate-800 rounded-lg">
              <p className="text-white font-mono">{load?.job_number}</p>
              <p className="text-slate-400 text-sm">{load?.pickup_city} → {load?.delivery_city}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-slate-700">Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-500">
              {deleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditLocalLoad;
